import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { operatorApi, type PraticaListItem, type ListPraticheParams } from '@services/api'
import { useOperatorAuth } from '@hooks/useOperatorAuth'
import { Button } from '@components/UI/Button'
import styles from './PraticheLista.module.css'

interface PraticheListaProps {
  onNewPratica?: () => void
}

function maskName(nome: string | null, cognome: string | null): string {
  const n = nome?.trim()
  const c = cognome?.trim()
  if (!n && !c) return '-'
  const maskPart = (s: string) => s.length <= 3 ? s.charAt(0) + '***' : s.slice(0, 3) + '***'
  const parts = []
  if (n) parts.push(maskPart(n))
  if (c) parts.push(maskPart(c))
  return parts.join(' ')
}

function maskCF(cf: string | null): string {
  if (!cf) return ''
  if (cf.length <= 6) return cf
  const visible = Math.min(6, cf.length)
  const tail = Math.min(4, cf.length - visible)
  return cf.slice(0, visible) + '*'.repeat(cf.length - visible - tail) + cf.slice(-tail)
}

function maskPhone(phone: string | null): string {
  if (!phone) return '-'
  const digits = phone.replace(/\D/g, '')
  if (digits.length <= 4) return '****'
  return '*'.repeat(digits.length - 4) + digits.slice(-4)
}

function maskEmail(email: string | null): string {
  if (!email) return '-'
  const atIdx = email.indexOf('@')
  if (atIdx <= 0) return '***'
  const show = Math.min(3, atIdx)
  return email.slice(0, show) + '***' + email.slice(atIdx)
}

const tipologiaLabels: Record<string, string> = {
  cambio_fornitore: 'Cambio Fornitore',
  voltura: 'Voltura', voltura_ordinaria: 'Voltura', voltura_mortis_causa: 'Voltura M.C.',
  subentro: 'Subentro', nuovo_allaccio: 'Nuovo Allaccio',
  riattivazione: 'Riattivazione', attivazione: 'Attivazione',
}

export function PraticheLista({ onNewPratica }: PraticheListaProps) {
  const navigate = useNavigate()
  const { operator: user } = useOperatorAuth()
  const isAdminLike = user?.role === 'admin' || user?.role === 'backoffice'
  const isPraticheOnly = user?.pratiche_only || user?.role === 'bo_ameconviene'
  // La duplica pratica e' disponibile a tutti i ruoli TRANNE gli operatori
  // (agent/operatore). Quindi: admin, supervisor, backoffice, bo_ameconviene.
  const canDuplicate = !!user?.role && !['operatore', 'operator', 'agent'].includes(user.role)

  const isBusiness = window.location.pathname.includes('/business')
  const basePath = isBusiness ? '/operatore/business' : '/operatore'

  const [pratiche, setPratiche] = useState<PraticaListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [tipoClienteFilter, setTipoClienteFilter] = useState<string>('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [perPage, setPerPage] = useState(20)

  const [visibleRows, setVisibleRows] = useState<Set<number>>(new Set())
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())

  const [duplicatingId, setDuplicatingId] = useState<number | null>(null)
  const [duplicateError, setDuplicateError] = useState<string | null>(null)

  const fetchPratiche = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const params: ListPraticheParams = { page, per_page: perPage }
      if (search.trim()) params.search = search.trim()
      if (statusFilter) params.status = statusFilter
      if (tipoClienteFilter) params.tipo_cliente = tipoClienteFilter as 'domestico' | 'business'
      if (dateFrom) params.from = dateFrom
      if (dateTo) params.to = dateTo

      const result = await operatorApi.listPratiche(params)
      setPratiche(result.data)
      setTotalPages(result.pagination.total_pages)
      setTotal(result.pagination.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento pratiche')
    } finally {
      setIsLoading(false)
    }
  }, [page, perPage, search, statusFilter, tipoClienteFilter, dateFrom, dateTo])

  useEffect(() => { fetchPratiche() }, [fetchPratiche])
  useEffect(() => { setPage(1) }, [search, statusFilter, tipoClienteFilter, dateFrom, dateTo])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    fetchPratiche()
  }

  const clearFilters = () => {
    setSearch('')
    setStatusFilter('')
    setTipoClienteFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  const toggleRowVisibility = (id: number) => {
    setVisibleRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectRow = (id: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedRows.size === pratiche.length) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(pratiche.map(p => p.id)))
    }
  }

  const formatDateShort = (date: string | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const getStatusBadge = (status: string) => {
    const cfg: Record<string, { label: string; cls: string; icon: string }> = {
      draft: { label: 'Bozza', cls: styles.statusDraft, icon: 'bi-pencil' },
      pending: { label: 'In attesa', cls: styles.statusPending, icon: 'bi-clock' },
      completed: { label: 'Completata', cls: styles.statusCompleted, icon: 'bi-check-circle-fill' },
      cancelled: { label: 'Annullata', cls: styles.statusCancelled, icon: 'bi-x-circle' }
    }
    const c = cfg[status] || cfg.draft
    return <span className={`${styles.badge} ${c.cls}`}><i className={`bi ${c.icon}`} /> {c.label}</span>
  }

  const getTipoBadge = (tipo: string) => {
    const cfg: Record<string, { label: string; cls: string; icon: string }> = {
      luce: { label: 'Luce', cls: styles.tipoLuce, icon: 'bi-lightning-charge-fill' },
      gas: { label: 'Gas', cls: styles.tipoGas, icon: 'bi-fire' },
      entrambi: { label: 'Dual', cls: styles.tipoEntrambi, icon: 'bi-layers-fill' }
    }
    const c = cfg[tipo] || cfg.luce
    return <span className={`${styles.badge} ${c.cls}`}><i className={`bi ${c.icon}`} /> {c.label}</span>
  }

  const getTipoClienteBadge = (tipo?: string) => {
    if (tipo === 'business') {
      return <span className={`${styles.badge} ${styles.tipoClienteBusiness}`}><i className="bi bi-building" /> Business</span>
    }
    return <span className={`${styles.badge} ${styles.tipoClienteDomestico}`}><i className="bi bi-house-door" /> Domestico</span>
  }

  const getTipologiaBadge = (p: PraticaListItem) => {
    const luce = p.tipo_attivazione_luce
    const gas = p.tipo_attivazione_gas
    if (!luce && !gas) return <span className={styles.tipologiaEmpty}>-</span>
    const labelL = luce ? tipologiaLabels[luce] || luce : null
    const labelG = gas ? tipologiaLabels[gas] || gas : null
    if (labelL && labelG && labelL === labelG) {
      return <span className={`${styles.badge} ${styles.tipologiaBadge}`}><i className="bi bi-arrow-repeat" /> {labelL}</span>
    }
    return (
      <div className={styles.tipologiaGroup}>
        {labelL && <span className={`${styles.badge} ${styles.tipologiaBadge}`}><i className="bi bi-lightning-charge-fill" /> {labelL}</span>}
        {labelG && <span className={`${styles.badge} ${styles.tipologiaBadge}`}><i className="bi bi-fire" /> {labelG}</span>}
      </div>
    )
  }

  const handleRowClick = (p: PraticaListItem) => {
    navigate(`${basePath}/pratica/${p.id}`)
  }

  const handleExportAll = async () => {
    setIsExporting(true)
    try {
      const params: ListPraticheParams & { id?: number } = {}
      if (search.trim()) params.search = search.trim()
      if (statusFilter) params.status = statusFilter
      if (tipoClienteFilter) params.tipo_cliente = tipoClienteFilter as 'domestico' | 'business'
      if (dateFrom) params.from = dateFrom
      if (dateTo) params.to = dateTo
      await operatorApi.exportPratiche(params)
    } catch (err) {
      console.error('Export error:', err)
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportSingle = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    try {
      await operatorApi.exportPratiche({ id })
    } catch (err) {
      console.error('Export error:', err)
    }
  }

  const handleDuplicate = async (e: React.MouseEvent, p: PraticaListItem) => {
    e.stopPropagation()
    if (duplicatingId) return
    setDuplicatingId(p.id)
    setDuplicateError(null)
    try {
      const { duplicated_from, source } = await operatorApi.duplicatePratica(p.id)
      // La pratica duplicata segue la tipologia dell'originale (business/domestico).
      const target = source.tipo_cliente === 'business' ? '/operatore/business' : '/operatore'
      navigate(target, { state: { duplicatePrefill: source, duplicatedFrom: duplicated_from } })
    } catch (err) {
      setDuplicateError(err instanceof Error ? err.message : 'Errore durante la duplica della pratica')
    } finally {
      setDuplicatingId(null)
    }
  }

  const isVisible = (id: number) => visibleRows.has(id)

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h1>{isAdminLike ? 'Tutte le Pratiche' : isPraticheOnly ? 'Pratiche' : 'Pratiche Operatore'}</h1>
          <span className={styles.totalCount}>{total} pratiche</span>
        </div>
        <div className={styles.headerActions}>
          {isAdminLike && (
            <button type="button" className={styles.exportBtn} onClick={handleExportAll} disabled={isExporting || total === 0}>
              <i className={`bi ${isExporting ? 'bi-arrow-repeat' : 'bi-file-earmark-excel'}`} />
              {isExporting ? 'Esportazione...' : 'Esporta Excel'}
            </button>
          )}
          {onNewPratica && !isPraticheOnly && (
            <Button variant="primary" icon="plus" onClick={onNewPratica}>Nuova Pratica</Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <div className={styles.searchInput}>
            <i className="bi bi-search" />
            <input type="text" placeholder="Cerca per nome, cognome, CF, ragione sociale, P.IVA, operatore..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </form>

        <div className={styles.filterGroup}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={styles.filterSelect}>
            <option value="">Tutti gli stati</option>
            <option value="draft">Bozza</option>
            <option value="pending">In attesa</option>
            <option value="completed">Completata</option>
            <option value="cancelled">Annullata</option>
          </select>

          <select value={tipoClienteFilter} onChange={(e) => setTipoClienteFilter(e.target.value)} className={styles.filterSelect}>
            <option value="">Tutti i clienti</option>
            <option value="domestico">Domestico</option>
            <option value="business">Business</option>
          </select>

          <div className={styles.dateFilter}>
            <label>Dal:</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className={styles.dateFilter}>
            <label>Al:</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          {(search || statusFilter || tipoClienteFilter || dateFrom || dateTo) && (
            <button type="button" className={styles.clearBtn} onClick={clearFilters}>
              <i className="bi bi-x-lg" /> Azzera
            </button>
          )}
        </div>
      </div>

      {/* Selection bar */}
      {isAdminLike && selectedRows.size > 0 && (
        <div className={styles.selectionBar}>
          <span><strong>{selectedRows.size}</strong> {selectedRows.size === 1 ? 'pratica selezionata' : 'pratiche selezionate'}</span>
          <div className={styles.selectionBarActions}>
            <button type="button" className={styles.selectionBarBtn} onClick={handleExportAll}>
              <i className="bi bi-file-earmark-excel" /> Esporta selezionati
            </button>
            <button type="button" className={styles.selectionBarClear} onClick={() => setSelectedRows(new Set())}>
              <i className="bi bi-x-lg" /> Deseleziona
            </button>
          </div>
        </div>
      )}

      {duplicateError && (
        <div className={styles.selectionBar} style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
          <span style={{ color: '#b91c1c' }}><i className="bi bi-exclamation-triangle" /> {duplicateError}</span>
          <div className={styles.selectionBarActions}>
            <button type="button" className={styles.selectionBarClear} onClick={() => setDuplicateError(null)}>
              <i className="bi bi-x-lg" /> Chiudi
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <>
          {/* Desktop skeleton */}
          <div className={styles.skeletonWrapper}>
            <div className={styles.skeletonHeader}>
              {[140, 80, 90, 80, 70, 80, 60, 70].map((w, i) => (
                <div key={i} className={styles.skeletonCell} style={{ width: w, height: 10, opacity: 0.6 }} />
              ))}
            </div>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={styles.skeletonRow} style={{ animationDelay: `${i * 0.08}s` }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '0 0 160px' }}>
                  <div className={styles.skeletonCell} style={{ width: 120 + (i % 3) * 20 }} />
                  <div className={styles.skeletonCell} style={{ width: 100, height: 10, opacity: 0.5 }} />
                </div>
                <div className={styles.skeletonCellRound} style={{ width: 72 + (i % 2) * 16 }} />
                <div className={styles.skeletonCell} style={{ width: 90, flex: '0 0 90px' }} />
                <div className={styles.skeletonCell} style={{ width: 70 + (i % 3) * 10, flex: '0 0 80px' }} />
                <div className={styles.skeletonCellRound} style={{ width: 56 + (i % 2) * 20 }} />
                <div className={styles.skeletonCellRound} style={{ width: 80 + (i % 3) * 15 }} />
                <div className={styles.skeletonCellRound} style={{ width: 68 + (i % 2) * 12 }} />
                <div className={styles.skeletonCell} style={{ width: 70, flex: '0 0 70px' }} />
              </div>
            ))}
          </div>
          {/* Mobile skeleton */}
          <div className={styles.skeletonCardList}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={styles.skeletonCard} style={{ animationDelay: `${i * 0.1}s` }}>
                <div className={styles.skeletonCardBadges}>
                  <div className={styles.skeletonCellRound} style={{ width: 70 }} />
                  <div className={styles.skeletonCellRound} style={{ width: 50 }} />
                  <div className={styles.skeletonCellRound} style={{ width: 80 }} />
                </div>
                <div className={styles.skeletonCardBody}>
                  <div className={styles.skeletonCell} style={{ width: '60%' }} />
                  <div className={styles.skeletonCell} style={{ width: '40%', height: 10, opacity: 0.5 }} />
                  <div className={styles.skeletonCell} style={{ width: '50%', height: 10, opacity: 0.5 }} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : error ? (
        <div className={styles.error}>
          <div className={styles.errorIcon}><i className="bi bi-exclamation-triangle" /></div>
          <h3>Errore di caricamento</h3>
          <p>{error}</p>
          <Button variant="secondary" icon="arrow-clockwise" onClick={fetchPratiche}>Riprova</Button>
        </div>
      ) : pratiche.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}><i className="bi bi-inbox" /></div>
          <h3>Nessuna pratica trovata</h3>
          <p>{search || statusFilter || dateFrom || dateTo
            ? 'Nessun risultato corrisponde ai filtri selezionati. Prova a modificarli o azzera la ricerca.'
            : 'Non ci sono ancora pratiche create in modalita operatore. Inizia creando la prima.'
          }</p>
          {onNewPratica && !isPraticheOnly && <Button variant="primary" icon="plus" onClick={onNewPratica}>Nuova Pratica</Button>}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {isAdminLike && (
                    <th className={styles.checkboxCell}>
                      <input
                        type="checkbox"
                        className={styles.rowCheckbox}
                        checked={selectedRows.size === pratiche.length && pratiche.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                  )}
                  <th>Cliente</th>
                  <th>Tipo Cliente</th>
                  <th>Telefono</th>
                  <th>Operatore</th>
                  <th>Tipo</th>
                  <th>Tipologia</th>
                  <th>Stato</th>
                  <th>Creata il</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pratiche.map((p) => {
                  const visible = isVisible(p.id)
                  return (
                    <tr key={p.id} className={styles.clickableRow} onClick={() => handleRowClick(p)}>
                      {isAdminLike && (
                        <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className={styles.rowCheckbox}
                            checked={selectedRows.has(p.id)}
                            onChange={() => toggleSelectRow(p.id)}
                          />
                        </td>
                      )}
                      <td>
                        <div className={styles.clienteInfo}>
                          <span className={visible ? styles.clienteNome : `${styles.clienteNome} ${styles.maskedText}`}>
                            {visible
                              ? `${p.anagrafica.nome || '-'} ${p.anagrafica.cognome || '-'}`
                              : maskName(p.anagrafica.nome, p.anagrafica.cognome)
                            }
                          </span>
                          {p.anagrafica.codice_fiscale && (
                            <span className={visible ? styles.clienteCf : `${styles.clienteCf} ${styles.maskedText}`}>
                              {visible ? p.anagrafica.codice_fiscale : maskCF(p.anagrafica.codice_fiscale)}
                            </span>
                          )}
                          {p.anagrafica.email && (
                            <span className={visible ? styles.clienteEmail : `${styles.clienteEmail} ${styles.maskedText}`}>
                              {visible ? p.anagrafica.email : maskEmail(p.anagrafica.email)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{getTipoClienteBadge(p.tipo_cliente)}</td>
                      <td className={visible ? styles.contactCell : `${styles.contactCell} ${styles.maskedText}`}>
                        {visible ? (p.anagrafica.telefono || '-') : maskPhone(p.anagrafica.telefono)}
                      </td>
                      <td className={styles.operatorCell}>
                        {p.operator_username || <span className={styles.operatorEmpty}>-</span>}
                      </td>
                      <td>{getTipoBadge(p.tipo_fornitura)}</td>
                      <td>{getTipologiaBadge(p)}</td>
                      <td>{getStatusBadge(p.status)}</td>
                      <td className={styles.dateCell}>{formatDateShort(p.created_at)}</td>
                      <td className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className={`${styles.visibilityBtn} ${visible ? styles.visibilityBtnActive : ''}`}
                          onClick={() => toggleRowVisibility(p.id)}
                          title={visible ? 'Nascondi dati' : 'Mostra dati'}
                        >
                          <i className={`bi ${visible ? 'bi-eye-fill' : 'bi-eye-slash'}`} />
                        </button>
                        {p.signing?.signed_pdf_available && p.signing?.session_id && (
                          <a
                            href={`/backend/api/v1/download-signed/${p.signing.session_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.exportRowBtn}
                            title="Scarica PDF firmato"
                          >
                            <i className="bi bi-file-earmark-pdf" />
                          </a>
                        )}
                        {isAdminLike && (
                          <button
                            type="button"
                            className={styles.exportRowBtn}
                            onClick={(e) => handleExportSingle(e, p.id)}
                            title="Esporta Excel"
                          >
                            <i className="bi bi-download" />
                          </button>
                        )}
                        {canDuplicate && p.status === 'completed' && (
                          <button
                            type="button"
                            className={styles.exportRowBtn}
                            onClick={(e) => handleDuplicate(e, p)}
                            disabled={duplicatingId === p.id}
                            title="Duplica pratica (correggi e rimanda al cliente)"
                          >
                            <i className={`bi ${duplicatingId === p.id ? 'bi-arrow-repeat' : 'bi-files'}`} />
                          </button>
                        )}
                        <button type="button" className={styles.viewBtn} onClick={() => handleRowClick(p)}>
                          <i className="bi bi-arrow-right" /> Dettagli
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className={styles.cardList}>
            {pratiche.map((p) => {
              const visible = isVisible(p.id)
              return (
                <div key={p.id} className={styles.card} onClick={() => handleRowClick(p)}>
                  <div className={styles.cardTop}>
                    {isAdminLike && (
                      <input
                        type="checkbox"
                        className={styles.rowCheckbox}
                        checked={selectedRows.has(p.id)}
                        onChange={() => toggleSelectRow(p.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <div className={styles.cardHeader}>
                      <div className={styles.cardBadges}>
                        {getTipoClienteBadge(p.tipo_cliente)}
                        {getTipoBadge(p.tipo_fornitura)}
                        {getTipologiaBadge(p)}
                        {getStatusBadge(p.status)}
                      </div>
                      <span className={styles.cardDate}>{formatDateShort(p.created_at)}</span>
                    </div>
                  </div>
                  <div className={styles.cardBody}>
                    <div className={visible ? styles.cardCliente : `${styles.cardCliente} ${styles.maskedText}`}>
                      <i className="bi bi-person" />
                      <span>
                        {visible
                          ? `${p.anagrafica.nome || '-'} ${p.anagrafica.cognome || '-'}`
                          : maskName(p.anagrafica.nome, p.anagrafica.cognome)
                        }
                      </span>
                    </div>
                    {p.anagrafica.codice_fiscale && (
                      <div className={visible ? styles.cardCf : `${styles.cardCf} ${styles.maskedText}`}>
                        {visible ? p.anagrafica.codice_fiscale : maskCF(p.anagrafica.codice_fiscale)}
                      </div>
                    )}
                    {p.anagrafica.telefono && (
                      <div className={visible ? styles.cardContact : `${styles.cardContact} ${styles.maskedText}`}>
                        <i className="bi bi-telephone" />
                        {visible ? p.anagrafica.telefono : maskPhone(p.anagrafica.telefono)}
                      </div>
                    )}
                    {p.anagrafica.email && (
                      <div className={visible ? styles.cardContact : `${styles.cardContact} ${styles.maskedText}`}>
                        <i className="bi bi-envelope" />
                        {visible ? p.anagrafica.email : maskEmail(p.anagrafica.email)}
                      </div>
                    )}
                    {p.operator_username && (
                      <div className={styles.cardOperator}>
                        <i className="bi bi-headset" />
                        {p.operator_username}
                      </div>
                    )}
                  </div>
                  <div className={styles.cardFooter}>
                    <div className={styles.cardFooterLeft}>
                      <button
                        type="button"
                        className={`${styles.visibilityBtn} ${visible ? styles.visibilityBtnActive : ''}`}
                        onClick={(e) => { e.stopPropagation(); toggleRowVisibility(p.id) }}
                        title={visible ? 'Nascondi dati' : 'Mostra dati'}
                      >
                        <i className={`bi ${visible ? 'bi-eye-fill' : 'bi-eye-slash'}`} />
                      </button>
                      {p.signing?.signed_pdf_available && p.signing?.session_id && (
                        <a
                          href={`/backend/api/v1/download-signed/${p.signing.session_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.exportRowBtn}
                          onClick={(e) => e.stopPropagation()}
                          title="Scarica PDF firmato"
                        >
                          <i className="bi bi-file-earmark-pdf" />
                        </a>
                      )}
                      {isAdminLike && (
                        <button
                          type="button"
                          className={styles.exportRowBtn}
                          onClick={(e) => { e.stopPropagation(); handleExportSingle(e, p.id) }}
                          title="Esporta Excel"
                        >
                          <i className="bi bi-download" />
                        </button>
                      )}
                      {canDuplicate && p.status === 'completed' && (
                        <button
                          type="button"
                          className={styles.exportRowBtn}
                          onClick={(e) => handleDuplicate(e, p)}
                          disabled={duplicatingId === p.id}
                          title="Duplica pratica (correggi e rimanda al cliente)"
                        >
                          <i className={`bi ${duplicatingId === p.id ? 'bi-arrow-repeat' : 'bi-files'}`} />
                        </button>
                      )}
                    </div>
                    <button type="button" className={styles.cardBtn} onClick={(e) => { e.stopPropagation(); handleRowClick(p) }}>
                      <i className="bi bi-arrow-right" /> Dettagli
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button type="button" disabled={page === 1} onClick={() => setPage(p => p - 1)} className={styles.pageBtn}>
                <i className="bi bi-chevron-left" />
              </button>
              {(() => {
                const pages: (number | 'ellipsis')[] = []
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i)
                } else {
                  pages.push(1)
                  if (page > 3) pages.push('ellipsis')
                  const start = Math.max(2, page - 1)
                  const end = Math.min(totalPages - 1, page + 1)
                  for (let i = start; i <= end; i++) pages.push(i)
                  if (page < totalPages - 2) pages.push('ellipsis')
                  pages.push(totalPages)
                }
                return pages.map((p, idx) =>
                  p === 'ellipsis' ? (
                    <span key={`e${idx}`} className={styles.pageEllipsis}>...</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className={`${styles.pageNum} ${p === page ? styles.pageNumActive : ''}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  )
                )
              })()}
              <button type="button" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className={styles.pageBtn}>
                <i className="bi bi-chevron-right" />
              </button>
              <div className={styles.paginationMeta}>
                <span>{total} pratiche</span>
                <select
                  className={styles.perPageSelect}
                  value={perPage}
                  onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1) }}
                >
                  <option value={15}>15 / pag</option>
                  <option value={50}>50 / pag</option>
                  <option value={100}>100 / pag</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
